import { stashHashAndStripUrl } from '@core/auth/utils/login-hash-stash.service';

import { environment } from '../../../../environments/environment';

/** Strongly-typed shape of the Keycloak section of the runtime/build environment. */
export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
  enabled: boolean;
}

/**
 * Read the runtime Keycloak configuration injected by entrypoint.sh into
 * `window.__env.keycloak`. Falls back to the build-time value compiled from
 * the active `environment.*.ts` file when running outside Docker.
 */
function getKeycloakConfig(): KeycloakConfig {
  if (typeof window !== 'undefined' && window.__env?.keycloak) {
    try {
      return JSON.parse(window.__env.keycloak);
    } catch {
      // Fall through to the build-time config; corrupt configmaps must not crash boot.
      console.warn('Failed to parse runtime keycloak config, using build-time config');
    }
  }

  return environment.keycloak;
}

/** Effective Keycloak configuration for the current session. */
export const KEYCLOAK_CONFIG: KeycloakConfig = getKeycloakConfig();

/**
 * Init options handed to keycloak-js via `provideKeycloak({ initOptions })`.
 *
 * Online → `login-required`: forces a full-page redirect to the Keycloak login
 * page when no session exists. If a SSO cookie is present on the realm,
 * Keycloak replies almost instantly and bounces the user back authenticated.
 *
 * Offline → no `onLoad`: keycloak-js initializes locally without any network
 * round-trip, fires Ready with authenticated=false, and UserProfileService
 * falls back to the cached profile in localStorage.
 */
export function getKeycloakInitOptions() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return {
      checkLoginIframe: false,
      pkceMethod: 'S256' as const,
    };
  }

  return {
    onLoad: 'login-required' as const,
    checkLoginIframe: false,
    pkceMethod: 'S256' as const,
    // 'query' keeps the URL hash free for the secondary-pane route. keycloak-js
    // consumes only its own code/state/session_state/iss query params and
    // leaves app query params and the hash untouched.
    responseMode: 'query' as const,
    // Explicit redirectUri without the hash: fragments are illegal in OAuth
    // redirect URIs. The hash is stashed and restored after init
    // (ScreensService.restoreLoginStashOnce).
    redirectUri: typeof window !== 'undefined' ? stashHashAndStripUrl(window.location.href) : undefined,
  };
}
